import { Link } from "react-router";
import { Trans, useTranslation } from "react-i18next";
import { CopyField } from "../components/CopyField";
import { Card, CardHeader, Mono, PageHeader } from "../components/ui";
import { inferenceBaseUrl } from "../lib/inference";
import { API_URL } from "../lib/api";
import { ApiKeysSection } from "./security/ApiKeysSection";

/**
 * API keys, on a page of their own.
 *
 * They were a tab on Security, beside two-factor authentication, and the nav's own
 * reasoning is what makes that wrong: Security sits in the *user* menu "because a
 * password and a second factor really are the account's". An API key is not the
 * account's. It belongs to the organization, it carries a role that scopes what it
 * can do to that organization's apps and databases, and since inference shipped it
 * spends that organization's credit. Two of those three are things a person cannot
 * do to themselves.
 *
 * So it moves to the organization rail, beside Team and Billing — the other pages
 * that configure the organization rather than a workload. Integrations was among
 * them until it moved into the user menu by request, which is a placement rather
 * than a change of what it belongs to. The tab
 * it leaves behind on Security is gone too; with only the account's own credentials
 * left there, a two-tab bar was navigation for its own sake.
 *
 * The endpoints are on the page rather than only in the docs because this is where
 * somebody stands when they need them: they have just created a key, and the next
 * thing they need is the address to send it to.
 */
export function ApiKeysPage() {
  const { t } = useTranslation("settings");
  return (
    <div className="space-y-6">
      <PageHeader
        title={t("apiKeys.title")}
        description={t("apiKeys.description")}
      />

      <Card>
        <CardHeader
          title={t("apiKeys.endpoints.title")}
          description={t("apiKeys.endpoints.description")}
        />
        <div className="grid gap-5 p-5 sm:grid-cols-2">
          <div className="space-y-1.5">
            <p className="micro">{t("apiKeys.inference.label")}</p>
            <CopyField value={inferenceBaseUrl()} label={t("apiKeys.inference.field")} />
            <p className="text-xs leading-5 text-fg-subtle">
              <Trans
                ns="settings"
                i18nKey="apiKeys.inference.note"
                components={{
                  catalogue: <Link to="/ai/models" className="text-accent underline underline-offset-2" />,
                }}
              />
            </p>
          </div>

          <div className="space-y-1.5">
            <p className="micro">{t("apiKeys.platform.label")}</p>
            <CopyField value={`${API_URL}/v1`} label={t("apiKeys.platform.field")} />
            <p className="text-xs leading-5 text-fg-subtle">{t("apiKeys.platform.note")}</p>
          </div>
        </div>
        {/*
          The distinction worth stating once, plainly: a key that can read is also a
          key that can spend, because inference authenticates with the same credential
          and every role can call it. Somebody scoping a key to `viewer` for a
          read-only script may reasonably expect otherwise. What *does* bound
          inference is the key's own limit and allowlist, which is why the sentence
          points there rather than at expiry alone.
        */}
        <p className="border-t border-line px-5 py-3 text-xs leading-5 text-fg-subtle">
          {t("apiKeys.roles")}
        </p>
      </Card>

      <ApiKeysSection />

      <p className="text-xs text-fg-subtle">
        <Trans
          ns="settings"
          i18nKey="apiKeys.security"
          components={{
            security: <Link to="/settings/security" className="text-accent underline underline-offset-2" />,
            code: <Mono />,
          }}
        />
      </p>
    </div>
  );
}
